import { useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'
import { useToast } from '../../system/ToastProvider.js'

export interface Asset {
  id: string
  name: string
}

export interface AssetSelectionModalProps {
  visible: boolean
  provider: string
  assets: Asset[]
  onSelect: (assetId: string, assetName: string) => Promise<void>
  onCancel: () => void
}

export default function AssetSelectionModal(props: AssetSelectionModalProps): JSX.Element {
  const toast = useToast()
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const selectedAsset = props.assets.find((a) => a.id === selectedAssetId)

  async function handleConfirm(): Promise<void> {
    if (!selectedAsset) return
    setSubmitting(true)
    try {
      await props.onSelect(selectedAsset.id, selectedAsset.name)
      props.onCancel()
    } catch (e) {
      toast.error('Failed to connect', e instanceof Error ? e.message : 'Failed to connect.')
    } finally {
      setSubmitting(false)
    }
  }

  function handleClose(): void {
    setSelectedAssetId(null)
    props.onCancel()
  }

  return (
    <Dialog
      visible={props.visible}
      header="Select Account"
      modal
      style={{ width: '450px' }}
      onHide={handleClose}
    >
      <div className="space-y-4">
        {props.assets.length === 0 ? (
          <>
            <p className="text-sm text-gray-600">
              No {props.provider} accounts were found. This usually means your {props.provider === 'facebook' ? 'Facebook account does not have any Pages' : `${props.provider} account does not have any business accounts`} associated with it.
            </p>
            <p className="text-sm text-gray-500">
              {props.provider === 'facebook' && 'Facebook Messenger works with Pages, not personal profiles. Please create a Facebook Page first, then try connecting again.'}
              {props.provider === 'instagram' && 'Instagram messaging requires an Instagram Business or Creator account linked to a Facebook Page.'}
              {props.provider === 'whatsapp' && 'WhatsApp Business requires a WhatsApp Business Account linked to a Meta Business Manager.'}
            </p>
            <div className="flex justify-end">
              <Button
                label="Close"
                onClick={handleClose}
                {...({ severity: 'secondary' } as Record<string, unknown>)}
              />
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              Select the {props.provider} account you want to connect:
            </p>

            <div className="space-y-2">
              {props.assets.map((asset) => (
                <div key={asset.id} className="flex items-center gap-3 rounded border p-3">
                  <RadioButton
                    inputId={`asset-${asset.id}`}
                    name="asset"
                    value={asset.id}
                    checked={selectedAssetId === asset.id}
                    onChange={(e) => setSelectedAssetId(e.value)}
                  />
                  <label htmlFor={`asset-${asset.id}`} className="flex-1 cursor-pointer">
                    <div className="font-medium">{asset.name}</div>
                    <div className="text-xs text-gray-500">ID: {asset.id}</div>
                  </label>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                label="Cancel"
                onClick={handleClose}
                disabled={submitting}
                {...({ severity: 'secondary' } as Record<string, unknown>)}
              />
              <Button
                label="Connect"
                onClick={() => void handleConfirm()}
                disabled={!selectedAsset || submitting}
                loading={submitting}
              />
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
